import { Job as ApiJob, JobHeader as ApiJobHeader } from "randmar-api-client";
import { IJob, IJobHeader } from "../../api/jobs/JobsApi";

/**
 * Type guard to differentiate between a legacy `IJob` and a new `ApiJob` from `randmar-api-client`.
 * @param job The job object to check.
 * @returns `true` if the job is an `ApiJob` (from `randmar-api-client`), `false` otherwise.
 */
export function isApiJob(job: IJob | ApiJob): job is ApiJob {
  return "Header" in job;
}

/**
 * Converts a new `ApiJobHeader` (from `randmar-api-client`) to a legacy `IJobHeader`.
 * This is useful for normalizing data during the migration period.
 * @param header The `ApiJobHeader` to convert.
 * @returns A normalized `IJobHeader`.
 */
export const fromApiJobHeader = (header: ApiJobHeader | undefined): IJobHeader => ({
  jobId: header?.JobId ?? "",
  status: header?.Status ?? "",
  description: header?.Description ?? "",
  type: header?.Type ?? "",
  createdAt: header?.CreatedAt ?? 0,
  applicationId: header?.ApplicationId ?? "",
  comment: header?.Comment,
  logs: header?.Logs,
  fileNames: header?.FileNames ?? [],
});

/**
 * Represents a job in a normalized format, acting as a compatibility layer
 * during the migration from legacy `IJob` to `ApiJob`.
 * This class ensures that job data can be handled consistently throughout the
 * application, regardless of its source.
 */
export default class Job {
  public readonly header?: IJobHeader;

  private constructor(source: IJob | ApiJob) {
    this.header = isApiJob(source) ? fromApiJobHeader(source.Header) : source.header;
  }

  /**
   * Creates a new `Job` instance from either a legacy `IJob` object or
   * a new `ApiJob` object from the randmar-api-client.
   * It normalizes the job header into the legacy `IJobHeader` format.
   * @param source The source job object (`IJob` or `ApiJob`).
   * @returns A new `Job` instance with a normalized header.
   */
  public static convertFrom(source: IJob | ApiJob): Job {
    return new Job(source);
  }
}
